"use client"

import { NearIntentsLogoSymbolIcon } from "@src/icons"
import { useBootstrapState } from "@src/providers/AppBootstrapProvider"
import type { ReactNode } from "react"

/**
 * States where the app is still initializing and children should not render.
 * Once the machine transitions out of these states, children are shown.
 */
const SPLASH_STATES = new Set([
  "booting",
  "leaving_login",
  "retrying_session",
  "redirecting_to_login",
])

export default function SplashScreen({ children }: { children: ReactNode }) {
  const machineState = useBootstrapState()

  if (SPLASH_STATES.has(machineState)) {
    return <SplashScreenContent />
  }

  return <>{children}</>
}

export function SplashScreenContent() {
  return (
    <div className="fade-in relative flex h-screen w-screen animate-in flex-col items-center justify-center gap-6 overflow-hidden bg-gray-800 duration-300 ease-out">
      <h1 className="sr-only">Loading application</h1>
      <NearIntentsLogoSymbolIcon className="size-18 shrink-0" />
      <ThreeDotsLoader />
    </div>
  )
}

const ThreeDotsLoader = () => (
  <div className="flex gap-1.5">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="size-1.5 animate-dot-bounce rounded-full bg-white/60"
        style={{ animationDelay: `${i * 160}ms` }}
      />
    ))}
  </div>
)
